import {FC, useCallback, useEffect, useState} from 'react'
import Particles from 'react-tsparticles'
import type {Engine, Container} from 'tsparticles-engine'
import {loadSlim} from 'tsparticles-slim'
import {loadTextShape} from 'tsparticles-shape-text'

const ACADEMIC_WHITE = '#f8f9fc'
// More visible: higher opacity and slightly stronger saturation
const SOFT_COLORS = [
  'rgba(248, 113, 113, 0.62)',
  'rgba(251, 146, 60, 0.58)',
  'rgba(96, 165, 250, 0.62)',
  'rgba(192, 132, 252, 0.58)',
  'rgba(52, 211, 153, 0.5)',
]

const EDUCATION_CHARS = ['📚', '📖', '✏️', '🎓', '📝', '📌', '📒', '✒️']

export const AnimatedBackground: FC = () => {
  const [reduceMotion, setReduceMotion] = useState(false)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mqReduce.matches)
    const handlerReduce = () => setReduceMotion(mqReduce.matches)
    mqReduce.addEventListener('change', handlerReduce)

    const mqMobile = window.matchMedia('(max-width: 768px)')
    setIsMobile(mqMobile.matches)
    const handlerMobile = () => setIsMobile(mqMobile.matches)
    mqMobile.addEventListener('change', handlerMobile)

    return () => {
      mqReduce.removeEventListener('change', handlerReduce)
      mqMobile.removeEventListener('change', handlerMobile)
    }
  }, [])

  const initParticles = useCallback(async (engine: Engine) => {
    await loadSlim(engine)
    await loadTextShape(engine)
  }, [])

  const particlesLoaded = useCallback(async (_container: Container | undefined) => {
    return
  }, [])

  const options = useCallback(
    () => {
      const noMotion = reduceMotion
      // Mobile: particles drift from center toward edges (continuous motion)
      const mobileMove = isMobile && !noMotion
      return {
        fullScreen: {enable: false},
        background: {
          color: {value: ACADEMIC_WHITE},
        },
        fpsLimit: 45,
        detectRetina: true,
        interactivity: {
          events: {
            onHover: {
              enable: !noMotion && !isMobile,
              mode: 'grab',
            },
            resize: true,
          },
          modes: {
            grab: {
              distance: 160,
              links: {
                opacity: 0.4,
                color: 'rgba(111, 163, 200, 0.5)',
              },
            },
          },
        },
        particles: {
          number: {
            value: noMotion ? 0 : isMobile ? 24 : 32,
            density: {enable: true, area: 800},
          },
          color: {
            value: SOFT_COLORS,
          },
          shape: {
            type: 'text',
            options: {
              text: {
                value: EDUCATION_CHARS,
                font: 'serif',
                style: '',
                weight: '400',
              },
            },
          },
          opacity: {
            value: {min: 0.52, max: 0.88},
            random: true,
          },
          size: {
            value: {min: 16, max: 26},
          },
          links: {
            enable: !noMotion && !isMobile,
            distance: 140,
            color: 'rgba(111, 163, 200, 0.35)',
            opacity: 0.35,
            width: 1,
          },
          move: {
            enable: !noMotion,
            speed: mobileMove ? {min: 0.4, max: 0.85} : {min: 0.2, max: 0.5},
            direction: mobileMove ? ('topRight' as const) : ('none' as const),
            random: !mobileMove,
            straight: mobileMove,
            outModes: mobileMove ? {default: 'out' as const} : {default: 'bounce' as const},
          },
        },
      }
    },
    [reduceMotion, isMobile]
  )

  return (
    <div className='marefati-bg' data-reduce-motion={reduceMotion || undefined}>
      <Particles
        key={`${reduceMotion}-${isMobile}`}
        id='marefati-tsparticles'
        className='marefati-bg__canvas'
        init={initParticles}
        loaded={particlesLoaded}
        options={options()}
      />
      <div className='marefati-bg__overlay' aria-hidden="true" />
    </div>
  )
}

export default AnimatedBackground
